import {
  GoogleMapsPrediction,
  GoogleMapsStructuredPlace
} from '../interfaces/googleMapsInterfaces'
import {
  getGoogleMapsPredictions,
  getPlaceDetailsById
} from '../services/googlePlacesServices'
import { useAutocompleteStore } from '../store'
import { formatGoogleAutoCompleteResponse } from '../utils/googleMapsUtils'

const WIGGOT_API_KEY = process.env.GOOGLE_MAPS_API_KEY ?? ''

// eslint-disable-next-line import/no-extraneous-dependencies
const { v4: uuidv4 } = require('uuid')

export function useAutocomplete() {
  const { setSessionToken } = useAutocompleteStore()

  const apiKey = WIGGOT_API_KEY

  const loadSessionToken = (): string => {
    const token = uuidv4()
    setSessionToken(token)

    return token
  }

  const clearSessionToken = (): void => {
    setSessionToken(undefined)
  }

  const findCoincidences = async (params: {
    label: string
    validateStreetNumber?: boolean
    limitedRegions?: string[]
    placeTypes?: string[]
    languageCode?: string
  }): Promise<{
    isSuccess: boolean
    data: GoogleMapsPrediction[] | undefined
  }> => {
    const {
      label,
      validateStreetNumber,
      limitedRegions = ['mx'],
      placeTypes = ['regions'],
      languageCode = 'es'
    } = params

    const forceStore = useAutocompleteStore.getState()

    let token = forceStore.sessionToken

    if (!token) {
      token = loadSessionToken()
    }

    const { isSuccess, data } = await getGoogleMapsPredictions({
      input: label,
      sessionToken: token,
      includedRegionCodes: limitedRegions,
      includedPrimaryTypes: placeTypes,
      languageCode,
      apiKey
    })

    if (!isSuccess || !data?.length) {
      return {
        isSuccess: false,
        data: undefined
      }
    }

    let predictions: GoogleMapsPrediction[] = [...data]

    if (validateStreetNumber) {
      predictions = filterByStreetNumber({
        predictions
      })
    }

    if (!predictions.length) {
      return {
        isSuccess: false,
        data: undefined
      }
    }

    return {
      isSuccess: true,
      data: predictions
    }
  }

  const filterByStreetNumber = (params: {
    predictions: GoogleMapsPrediction[]
  }): GoogleMapsPrediction[] => {
    const { predictions } = params

    const filter = predictions.filter(({ placePrediction }) => {
      const { text } = placePrediction
      return (
        text &&
        text?.text?.match(/\d/g) !== null &&
        text?.text?.match(/\d/g)?.length !== 0
      )
    })

    return filter
  }

  const selectPlace = async (params: {
    prediction: GoogleMapsPrediction
  }): Promise<GoogleMapsStructuredPlace | undefined> => {
    const { prediction } = params

    const { placeId } = prediction.placePrediction

    const forceStore = useAutocompleteStore.getState()

    let token = forceStore.sessionToken

    if (!token) {
      token = loadSessionToken()
    }

    const { isSuccess, data } = await getPlaceDetailsById({
      placeId,
      sessionToken: token,
      apiKey
    })

    clearSessionToken()

    if (!isSuccess || !data) {
      return undefined
    }

    const { placePrediction } = prediction

    const formatted = formatGoogleAutoCompleteResponse({
      place: data,
      originalfullAddress: placePrediction?.text?.text
    })

    return formatted
  }

  return {
    findCoincidences,
    selectPlace
  }
}
